<template>
  <!-- ── Icon mode ─────────────────────────────────────────────────────── -->
  <template v-if="mode === 'icon'">
    <div
      class="device-icon-pin"
      :class="sizeClass"
      :style="{ '--pin-active-color': 'var(--device-color-switch)' }"
      :title="device.deviceName">
      <!-- The drawn computer inside the status ring (P14): online fills the
           family's green ring, offline keeps the track. -->
      <ControlStatusRing :value="online ? 100 : 0">
        <ControlIcon
          name="computer"
          :size="pinIconSize"
          :style="online ? { color: 'var(--device-color-switch)' } : {}" />
      </ControlStatusRing>
    </div>
  </template>

  <!-- ── Grid mode ───────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'grid'">
    <DeviceGridCell
      :icon="device.icon || 'computer'"
      :name="displayName"
      :is-on="online"
      active-color="green-400"
      @toggle="emit('open-control-modal')"
      @long-press="emit('open-control-modal')" />
  </template>

  <!-- ── Control / full mode ───────────────────────────────────────────── -->
  <!-- No head of its own: the sheet's chrome paints the circle, the name and
       the online pill (card t_488df70b); the body starts at the readouts. -->
  <template v-else>
    <div class="computer-sheet">
      <!-- One tile per readout, in the sheet language — the same values the
           old cards carried (CPU / memory / disk, CPU temperature, uptime). -->
      <div class="computer-grid">
        <ControlValueTile
          v-for="metric in usageMetrics"
          :key="metric.key"
          :icon="metric.icon"
          :label="metric.name"
          :value="metric.display" />
        <ControlValueTile
          v-if="temperatureValue !== null"
          icon="thermometer"
          :label="tt('CPU temperature', 'Temperatura CPU')"
          :value="temperatureDisplay" />
        <ControlValueTile icon="clock" :label="tt('Uptime', 'Čas delovanja')" :value="uptimeDisplay" />
      </div>

      <p v-if="lastUpdatedLabel" class="computer-meta">
        <ControlIcon name="clock" size="0.8125rem" />
        {{ tt('Last updated', 'Zadnja posodobitev') }}: {{ lastUpdatedLabel }}
      </p>
    </div>
  </template>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { formatTime } from '../../utils/formatDate';

import ControlIcon from '../device-icons/ControlIcon.vue';
import ControlStatusRing from '../device-sheet/ControlStatusRing.vue';
import ControlValueTile from '../device-sheet/ControlValueTile.vue';
import { useDeviceValues } from '../../composables/useDeviceValues';
import DeviceGridCell from '../panel/DeviceGridCell.vue';
import { tt } from '../../utils/consoleText';
import type { Device, DeviceValueWithReferences } from '@/types';

interface Props {
  device: Device;
  mode?: 'icon' | 'control' | 'grid';
  size?: 'sm' | 'md' | 'lg';
  fluid?: boolean;
  displayMode?: 'full' | 'room';
}

const props = withDefaults(defineProps<Props>(), { mode: 'icon', size: 'md' });

const emit = defineEmits<{
  toggle: [];
  'open-control-modal': [];
  'view-stream': [];
  'view-details': [];
  'value-update': [valueId: string, newValue: unknown];
}>();

// ── Live values ──────────────────────────────────────────────────────────
const { currentValues, initializeValues } = useDeviceValues(props.device.id);
if (props.device.deviceValues?.length) initializeValues(props.device.deviceValues);
watch(
  () => props.device.deviceValues,
  (v) => {
    if (v?.length) initializeValues(v);
  },
  { deep: true },
);

function valueDef(key: string): DeviceValueWithReferences | undefined {
  return (props.device.deviceValues ?? []).find((v) => v.valueKey === key);
}

function readValue(key: string): unknown {
  if (key in currentValues.value) return currentValues.value[key];
  return valueDef(key)?.currentValue ?? undefined;
}

const onlineState = computed<boolean | undefined>(() => {
  const v = readValue('online');
  if (v === true || v === 1 || v === 'true' || v === 'on') return true;
  if (v === false || v === 0 || v === 'false' || v === 'off') return false;
  return undefined;
});
const online = computed<boolean>(() => onlineState.value === true);

function toNumber(raw: unknown): number | null {
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

interface UsageMetric {
  key: string;
  name: string;
  /** A ControlIcon name — never a Material glyph. */
  icon: string;
  display: string;
}

function buildMetric(key: string, fallbackName: string, icon: string): UsageMetric {
  const def = valueDef(key);
  const raw = readValue(key);
  const n = toNumber(raw);
  const name = def?.name || fallbackName;
  return {
    key,
    name,
    icon,
    display: n === null ? '—' : `${Math.round(n * 10) / 10}${def?.unit ? ` ${def.unit}` : ''}`,
  };
}

const usageMetrics = computed<UsageMetric[]>(() => [
  buildMetric('cpu_load', 'CPU', 'activity'),
  buildMetric('memory_used', tt('Memory', 'Pomnilnik'), 'server'),
  buildMetric('disk_used', 'Disk', 'server'),
]);

const temperatureValue = computed<number | null>(() => toNumber(readValue('cpu_temperature')));
const temperatureDisplay = computed(() => {
  if (temperatureValue.value === null) return '—';
  return `${temperatureValue.value.toFixed(1)} °C`;
});

const uptimeSeconds = computed<number | null>(() => toNumber(readValue('uptime_seconds')));
const uptimeDisplay = computed(() => formatUptime(uptimeSeconds.value));

const lastUpdatedLabel = computed(() => {
  let latest: number | null = null;
  for (const v of props.device.deviceValues ?? []) {
    const ts = v.lastUpdated || v.updatedAt;
    if (!ts) continue;
    const t = new Date(ts).getTime();
    if (Number.isFinite(t) && (latest === null || t > latest)) latest = t;
  }
  if (!latest) return '';
  return formatTime(new Date(latest));
});

// ── Display helpers ──────────────────────────────────────────────────────
const displayName = computed(() =>
  props.displayMode === 'full' ? props.device.deviceName : props.device.shortName || props.device.deviceName,
);

const sizeClass = computed(() => (props.fluid ? 'device-icon-pin--fluid' : `device-icon-pin--${props.size ?? 'md'}`));

/** The drawn computer's size inside the pin (P14), in the `GenericIcon` steps. */
const pinIconSize = computed(() => {
  if (props.fluid) return '28px';
  switch (props.size) {
    case 'lg':
      return '28px';
    case 'sm':
      return '16px';
    default:
      return '22px';
  }
});

function formatUptime(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds) || seconds < 0) return '—';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}
</script>

<style scoped>
/* Control mode: the readouts in the sheet language. The host chrome owns the
   head and the panel; this block is the tile grid and the meta line. */
.computer-sheet {
  display: flex;
  flex-direction: column;
  gap: calc(0.875rem * var(--ui-space-factor));
}

.computer-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: calc(0.5rem * var(--ui-space-factor));
}

@media (min-width: 640px) {
  .computer-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.computer-meta {
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375em;
  font-size: 0.75rem;
  color: rgb(var(--gray-500));
}

.dark .computer-meta {
  color: rgb(var(--gray-400));
}
</style>
